import { useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { cropToBlob } from '../lib/image-upload';
import { IMAGE_PRESETS, type ImageKind } from '../lib/image-presets';
import { Button, Modal } from './ui';

const ZOOM_MIN = 1;
const ZOOM_MAX = 4;
const ROTATION_STEP = 90;

/**
 * Кадрирование перед загрузкой: пропорции берутся из пресета, поэтому аватар
 * всегда квадратный, а баннер — широкий, независимо от исходного снимка.
 */
export function ImageCropper({
  source,
  kind,
  pending,
  error,
  onCancel,
  onCrop,
}: {
  source: string;
  kind: ImageKind;
  pending: boolean;
  error?: string | null;
  onCancel: () => void;
  onCrop: (blob: Blob) => void;
}) {
  const preset = IMAGE_PRESETS[kind];
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(ZOOM_MIN);
  const [rotation, setRotation] = useState(0);
  const [area, setArea] = useState<Area | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const apply = async () => {
    if (!area) {
      return;
    }

    try {
      onCrop(await cropToBlob(source, area, rotation));
    } catch (cropFailure) {
      setFailure(cropFailure instanceof Error ? cropFailure.message : 'Не удалось обрезать');
    }
  };

  return (
    <Modal title={preset.title} onClose={onCancel}>
      <div className="cropper">
        <Cropper
          image={source}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          aspect={preset.aspect ?? undefined}
          cropShape={preset.round ? 'round' : 'rect'}
          showGrid={!preset.round}
          restrictPosition={preset.aspect !== null}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onRotationChange={setRotation}
          onCropComplete={(_, pixels) => setArea(pixels)}
        />
      </div>

      <div className="cropper__controls">
        <label className="cropper__zoom">
          <span className="field__label">Масштаб</span>
          <input
            type="range"
            min={ZOOM_MIN}
            max={ZOOM_MAX}
            step={0.01}
            value={zoom}
            aria-label="Масштаб"
            onChange={(event) => setZoom(Number(event.target.value))}
          />
        </label>
        <Button
          variant="ghost"
          type="button"
          aria-label="Повернуть"
          onClick={() => setRotation((current) => (current + ROTATION_STEP) % 360)}
        >
          ↻ 90°
        </Button>
      </div>

      {(failure ?? error) && <p className="notice notice--error">{failure ?? error}</p>}

      <div className="stack">
        <Button wide type="button" disabled={pending || !area} onClick={() => void apply()}>
          {pending ? 'Загружаем…' : 'Готово'}
        </Button>
        <Button variant="ghost" wide type="button" disabled={pending} onClick={onCancel}>
          Отмена
        </Button>
      </div>
    </Modal>
  );
}
